// src/themes/modern/menu/components/FeaturedMenuSection.tsx
import { motion, type PanInfo } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaArrowLeft, FaArrowRight, FaStar } from "react-icons/fa";
import type { BranchData, MenuItem } from "~/types/new_types";
import MenuItemGridCard from "./MenuItemGridCard";
import { useBranch } from "~/contexts/BranchContext";
import { RiArrowLeftSLine, RiArrowRightSLine } from "react-icons/ri";

interface FeaturedMenuSectionProps {
  setSelectedItem: React.Dispatch<React.SetStateAction<MenuItem | null>>;
}

const FeaturedMenuSection: React.FC<FeaturedMenuSectionProps> = ({
  setSelectedItem,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const [currentIndex, setCurrentIndex] = useState(0);
  const [maxVisibleItems, setMaxVisibleItems] = useState(4);
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [cardWidth, setCardWidth] = useState(320);
  const [gapWidth, setGapWidth] = useState(16);
  const carouselRef = useRef<HTMLDivElement>(null);

  const { branchData } = useBranch();

  // Get all popular items from all categories
  const popularItems =
    branchData?.items
      ?.filter((item) => item.is_featured && item.is_available)
      .slice(0, 12) ?? [];

  // Calculate how many items to show based on screen size
  useEffect(() => {
    const handleResize = () => {
      const containerWidth =
        carouselRef.current?.parentElement?.clientWidth || 0;

      let itemsToShow = 5;
      if (window.innerWidth < 360) {
        itemsToShow = 1;
      } else if (window.innerWidth < 640) {
        itemsToShow = 2;
      } else if (window.innerWidth < 768) {
        itemsToShow = 3;
      } else if (window.innerWidth < 1024) {
        itemsToShow = 4;
      }

      setMaxVisibleItems(itemsToShow);

      // Calculate card width
      const totalGapWidth = gapWidth * (itemsToShow - 1);
      const calculatedCardWidth = Math.floor(
        (containerWidth - totalGapWidth) / itemsToShow
      );
      setCardWidth(calculatedCardWidth);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [gapWidth]);

  // Auto-scroll carousel unless hovered
  useEffect(() => {
    let autoScrollTimeout: NodeJS.Timeout;

    if (!isHovered && !isDragging && popularItems.length > maxVisibleItems) {
      autoScrollTimeout = setTimeout(() => {
        handleNext();
      }, 5000);
    }

    return () => clearTimeout(autoScrollTimeout);
  }, [
    currentIndex,
    isHovered,
    isDragging,
    popularItems.length,
    maxVisibleItems,
  ]);

  const handleNext = () => {
    if (currentIndex < popularItems.length - maxVisibleItems) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      setCurrentIndex(0);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    } else {
      setCurrentIndex(popularItems.length - maxVisibleItems);
    }
  };

  // Handle drag gestures for mobile
  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = (
    event: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo
  ) => {
    setIsDragging(false);

    const dragThreshold = 50; // Minimum drag distance to trigger slide
    const dragDistance = isRTL ? -info.offset.x : info.offset.x;

    if (Math.abs(dragDistance) > dragThreshold) {
      if (dragDistance > 0) {
        // Dragged right (previous)
        handlePrev();
      } else {
        // Dragged left (next)
        handleNext();
      }
    }
  };

  if (popularItems.length === 0) {
    return null;
  }

  // Calculate translation amount
  const translateX = isRTL
    ? currentIndex * (cardWidth + gapWidth)
    : -currentIndex * (cardWidth + gapWidth);

  return (
    <section className="pt-12 relative overflow-hidden">
      {/* Background decoration */}
      <div
        className="absolute inset-0 opacity-10 -z-10"
        style={{
          backgroundImage: `linear-gradient(to right, ${branchData?.branch.theme.primaryColor}20, ${branchData?.branch.theme.secondaryColor}20)`,
          backgroundSize: "100% 100%",
        }}
      />

      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <motion.div
          >
            <h2
              className={`${branchData?.branch.theme.titleFontSize} ${
                branchData?.branch.theme.boldTitles ? "font-bold" : ""
              } flex items-center gap-2`}
              style={{ color: branchData?.branch.theme.textColor }}
            >
              <FaStar
                style={{ color: branchData?.branch.theme.primaryColor }}
              />
              <span>{t("menu.featuredItems")}</span>
            </h2>
            <p style={{ color: `${branchData?.branch.theme.textColor}80` }}>
              {t("menu.featuredDescription")}
            </p>
          </motion.div>

          <div className="hidden md:flex gap-2">
            <motion.button
              onClick={handlePrev}
              className="w-10 h-10 rounded-full flex items-center justify-center cursor-pointer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              style={{
                backgroundColor: `${branchData?.branch.theme.primaryColor}20`,
                color: branchData?.branch.theme.textColor,
                borderRadius: branchData?.branch.theme.borderRadius,
              }}
              disabled={popularItems.length <= maxVisibleItems}
            >
              {isRTL ? <FaArrowRight /> : <FaArrowLeft />}
            </motion.button>
            <motion.button
              onClick={handleNext}
              className="w-10 h-10 rounded-full flex items-center justify-center cursor-pointer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              style={{
                backgroundColor: branchData?.branch.theme.primaryColor,
                color: "#fff",
                borderRadius: branchData?.branch.theme.borderRadius,
              }}
              disabled={popularItems.length <= maxVisibleItems}
            >
              {isRTL ? <FaArrowLeft /> : <FaArrowRight />}
            </motion.button>
          </div>
        </div>

        {/* Carousel */}
        <div className="relative overflow-hidden">
          <motion.div
            ref={carouselRef}
            className="flex pt-2"
            style={{ gap: `${gapWidth}px` }}
            animate={{ x: translateX }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            drag="x"
            dragElastic={0.1}
            dragMomentum={false}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            whileDrag={{ cursor: "grabbing" }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {popularItems.map((item, index) => (
              <div
                key={`${item.id}-${index}`}
                style={{ 
                  width: cardWidth,
                  flexShrink: 0
                }}
              >
                <MenuItemGridCard
                  item={item}
                  onClick={() => setSelectedItem(item)}
                />
              </div>
            ))}
          </motion.div>
        </div>

        {/* Indicator dots and mobile navigation */}
        {popularItems.length > maxVisibleItems && (
          <div className="flex md:hidden justify-center items-center gap-2 mt-6">
            <motion.button
              onClick={handlePrev}
              className="w-6 h-6 rounded-full flex items-center justify-center cursor-pointer me-3 md:hidden"
              whileHover={{
                scale: 1.1,
                color: branchData?.branch.theme.textColor,
              }}
              whileTap={{
                scale: 0.9,
                color: branchData?.branch.theme.textColor,
              }}
              style={{
                backgroundColor: `${branchData?.branch.theme.primaryColor}20`,
                color: `${branchData?.branch.theme.textColor}70`,
              }}
              disabled={popularItems.length <= maxVisibleItems}
            >
              {isRTL ? (
                <RiArrowRightSLine size={20} />
              ) : (
                <RiArrowLeftSLine size={20} />
              )}
            </motion.button>

            {/* Indicator dots */}
            <div className="flex gap-2">
              {Array.from({
                length: Math.ceil(popularItems.length / maxVisibleItems),
              }).map((_, index) => (
                <motion.button
                  key={index}
                  onClick={() => setCurrentIndex(index * maxVisibleItems)}
                  className="w-2 h-2 rounded-full cursor-pointer transition-all"
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                  style={{
                    backgroundColor:
                      Math.floor(currentIndex / maxVisibleItems) === index
                        ? branchData?.branch.theme.primaryColor
                        : `${branchData?.branch.theme.primaryColor}40`,
                  }}
                />
              ))}
            </div>

            <motion.button
              onClick={handleNext}
              className="w-6 h-6 rounded-full flex items-center justify-center cursor-pointer ms-3 md:hidden"
              whileHover={{
                scale: 1.1,
                color: branchData?.branch.theme.textColor,
              }}
              whileTap={{
                scale: 0.9,
                color: branchData?.branch.theme.textColor,
              }}
              style={{
                backgroundColor: `${branchData?.branch.theme.primaryColor}20`,
                color: `${branchData?.branch.theme.textColor}70`,
              }}
              disabled={popularItems.length <= maxVisibleItems}
            >
              {isRTL ? (
                <RiArrowLeftSLine size={20} />
              ) : (
                <RiArrowRightSLine size={20} />
              )}
            </motion.button>
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedMenuSection;
